import { computed, reactive } from 'vue';
import { defineStore } from 'pinia';
import { ChangeEditableCell, EditableCell, IBaseTableContentState, NarrowState } from './interface/uiState';
import {
  isBusinessesForSupplierForecastHeaderKey,
  isForecastForReceiptKey,
  isForecastAccuracyKey,
  isOrderControlHeaderKey,
  isOrderHeaderKey,
  isTrashBinHeaderKey,
  isUnmanagedBusinessHeaderKey,
  KeyType,
  TableContentHeader,
  TableContentProperty,
} from './interface/tableContentHeaders';
import { TableContentItem } from './interface/tableContentItems';
import { isIDate } from '@/coreDomainsModule/date';
import { simpleCopy } from '@/utils/deepCopy';
import { objectEquals } from '@/utils/objectEquals';
import { convertIDateToDateFormat } from './utils/dateFormat';
import { BaseTableContentViewModel } from './baseTableContentViewModel';
import { MAX_COLUMN_WIDTH, STICKY_COLUMN_WIDTH } from './constants';
import { GetColumnWidthsUseCase } from './useCases/getColumnWidthsUseCase';
import { GetTableHeaderPreferenceUseCase } from './useCases/getTableHeaderPreferenceUseCase';
import { SaveTableHeaderPreferenceUseCase } from './useCases/saveTableHeaderPreferenceUseCase';
import { isDefaultDisableHeaderList, isCommonTableHeaderKey, isHeaderModifiableTableType, TableType } from '@/coreDomainsModule/table';
import { ResizeStateService } from './services/resizeStateService';
import { BulkSaveColumnWidthsUseCase } from './useCases/bulkSaveColumnWidthsUseCase';
import { openFailToAcquireModal } from '@/corePagesModule/components/modal/modalActions';
import { LoadingState } from '@/coreDomainsModule/uiLoadingState';
import { TrashBinTableContentViewModel } from './components/trashBin/trashBinTableContentViewModel';
import { BusinessesForSupplierForecastTableContentViewModel } from './components/businessesForSupplierForecast/businessesForSupplierForecastTableContentViewModel';
import { UnmanagedBusinessContentViewModel } from './components/unmanagedBusiness/unmanagedBusinessTableContentViewModel';
import { ForecastForReceiptTableContentViewModel } from './components/customerForecast/forecastForReceiptTableContentViewModel';
import { ForecastAccuracyTableContentViewModel } from './components/customerForecast/forecastAccuracyTableContentViewModel';
import { i18n } from '@/i18n';
import { SaveTableHeaderHidePreferenceUseCase } from './useCases/saveTableHeaderHidePreferenceUseCase';
import { HeaderList } from '@/coreDomainsModule/table';
import { GetModifiableHeaderKeysUseCase } from './useCases/getModifiableHeaderKeysUseCase';
import { BulkSaveTableHeaderHidePreferenceUseCase } from './useCases/bulkSaveTableHeaderHidePreferenceUseCase';
import { diContainer } from '@/coreContainer/container';
import { isAggregateBusinessFreeColumnKey } from './utils/businessFreeColumnTable';

const baseTableContentVM = new BaseTableContentViewModel();
const getColumnWidthsUseCase = new GetColumnWidthsUseCase();
const bulkSaveColumnWidthsUseCase = new BulkSaveColumnWidthsUseCase();
const getTableHeaderPreferenceUseCase = new GetTableHeaderPreferenceUseCase();

/**
 * 確定発注画面のStoreを返す
 * NOTE: Storeは同一の名前でSingletonとして扱われるため、名前を変える必要がある
 * @param tableId - テーブルのユニークなID
 */
export const useBaseTableContentStore = (tableId: string) =>
  defineStore(`useBaseTableContent-${tableId}`, () => {
    const state = reactive<IBaseTableContentState>({
      tableContentProperties: {} as TableContentHeader,
      tableType: 'baseTable',
      accumulationTableContentWidth: [],
      items: [],
      loadingState: {
        status: {
          type: 'loading',
        },
      },
      isEditing: false,
      isAllSelected: false,
      selectedIds: [],
      narrowStates: [] as NarrowState[],
      resizeState: {
        resizingIndex: -1,
        currentMousePageX: 0,
      },
      changeEditableCellHistory: {},
      isOpeningAlertIndex: '',
      isOpenHeaderModifyButtonMenu: false,
    });

    /**
     * stateの初期化
     */
    const $reset = () => {
      state.tableContentProperties = {} as TableContentHeader;
      state.tableType = 'baseTable';
      state.accumulationTableContentWidth = [];
      state.items = [];
      state.loadingState = {
        status: {
          type: 'loading',
        },
      };
      state.isEditing = false;
      state.isAllSelected = false;
      state.selectedIds = [];
      state.narrowStates = [];
      state.resizeState = {
        resizingIndex: -1,
        currentMousePageX: 0,
      };
      state.changeEditableCellHistory = {};
      state.isOpeningAlertIndex = '';
      state.isOpenHeaderModifyButtonMenu = false;
    };

    const computedColumnsWidth = computed((): number => {
      return state.tableContentProperties.contentProperties.reduce((acc, contentProperty) => {
        return acc + (contentProperty.headerWidth ?? STICKY_COLUMN_WIDTH);
      }, 0);
    });

    /**
     * テーブルの内容を初期化する
     * @param propTableContentProperties - テーブルのヘッダー情報
     * @param propItems - テーブルのアイテム情報
     * @param propLoadingState - ローディング状態
     * @param propTableType - テーブルの種類
     * @param propIsEditing - 編集中かどうか
     */
    const initializeTableContent = async (
      propTableContentProperties: TableContentHeader,
      propItems: TableContentItem[],
      propLoadingState: LoadingState,
      propTableType: TableType,
      propIsEditing: boolean
    ) => {
      state.loadingState = propLoadingState;
      state.isEditing = propIsEditing;

      const { contentProperties, ...buf } = propTableContentProperties;

      const bufTableContentProperties = {
        contentProperties: [],
        ...buf,
      };

      state.tableContentProperties = bufTableContentProperties;

      state.items = propItems;
      state.tableType = propTableType;
      // propsが渡ってくるタイミングの関係で、これらがundefinedの場合があるので回避
      if (state.tableContentProperties && state.tableContentProperties.contentProperties && state.tableContentProperties.options) {
        if (state.tableContentProperties.options.resizableHeaderColumn) {
          // ヘッダーの幅を計算してセット
          _calcAndSetHeaderWidth();
        }
        await _initializeColumnWidth(propTableContentProperties.contentProperties);
        await saveTableHeaderPreference(propTableContentProperties.contentProperties);
      }
      // DBからのヘッダー情報の取得に失敗したらPropのデータをそのまま書き込む
      if (!state.tableContentProperties.contentProperties.length) {
        if (propTableContentProperties && propTableContentProperties.contentProperties) {
          state.tableContentProperties = {
            contentProperties: propTableContentProperties.contentProperties.filter(
              (contentProperty) => !isDefaultDisableHeaderList(contentProperty.key)
            ),
            options: propTableContentProperties.options,
            forecastStartYearMonth: propTableContentProperties.forecastStartYearMonth,
          };
          await _bulkUpdateDisplayHeader(
            propTableType,
            propTableContentProperties.contentProperties.flatMap((property) =>
              isDefaultDisableHeaderList(property.key) ? property.key : []
            ),
            true
          );
        }
      }
    };

    /**
     * 列幅の初期化
     * 列幅をDBに保持している場合、それを取得して反映する
     * @param propContentProperties - Propで渡されたヘッダー情報
     */
    const _initializeColumnWidth = async (propContentProperties: TableContentProperty[]) => {
      // 列幅及びヘッダーのリストをDBから取得
      const columnWidthResult = await getColumnWidthsUseCase.Execute(state.tableType);
      const headerListResult = await getTableHeaderPreferenceUseCase.Execute(state.tableType);
      if (columnWidthResult.status.type === 'success' && headerListResult.status.type === 'success') {
        const columnWidths = columnWidthResult.status.data;
        const headerList = headerListResult.status.data.headerList;
        // DBにヘッダー情報がある場合は、ヘッダー情報を更新
        if (headerList)
          state.tableContentProperties.contentProperties = propContentProperties.filter((property) => headerList.includes(property.key));
        if (objectEquals(headerList, baseTableContentVM.getCurrentHeaderList(state.tableContentProperties))) {
          // keyの追加などヘッダー配列に変更があった場合、保持されていた列幅は無視
          // 列幅はkey, valueで保持されている
          for (const key of Object.keys(columnWidths)) {
            const index = headerList.indexOf(key);
            if (index === -1) continue;
            // 固定幅の列は無視
            if (
              !baseTableContentVM.isSticky(state.tableContentProperties.options.stableColumnCount, index) ||
              !state.tableContentProperties.contentProperties[index].isVariableWidth
            )
              state.tableContentProperties.contentProperties[index].headerWidth = ResizeStateService.getResizableColumnWidth(
                state.tableType,
                state.tableContentProperties.options.stableColumnCount,
                columnWidths[key]
              );
          }
        }
      }
    };

    /**
     * ヘッダーの並び順をDBに保存する
     * @param propContentProperties - Propで渡されたヘッダー情報
     */
    const saveTableHeaderPreference = async (propContentProperties: TableContentProperty[]) => {
      await diContainer.saveTableHeaderPreferenceUseCase.Execute(
        state.tableType,
        propContentProperties.map((property) => property.key)
      );
    };

    /**
     * 親コンポーネントで動的に更新されたヘッダーを、表示・非表示設定を反映して同期する
     * @param propContentProperties - Propで渡されたヘッダー情報
     */
    const synchronizeTableContentProperties = async (propContentProperties: TableContentProperty[]) => {
      await saveTableHeaderPreference(propContentProperties);
      await _initializeColumnWidth(propContentProperties);
    };

    /**
     * ヘッダーの幅を計算してセットする
     */
    const _calcAndSetHeaderWidth = () => {
      // テーブルの幅を取得する
      const useTableContentHeader = simpleCopy(state.tableContentProperties);
      const resizableColumnsSpaceWidth = ResizeStateService.getResizableColumnsSpaceWidth(
        state.tableType,
        useTableContentHeader.options.stableColumnCount
      );
      if (!resizableColumnsSpaceWidth) return;
      // 余白から各可変幅に割り振れる幅を計算
      // (余白の幅) / ((列数) - (列幅変更不可 && 列幅が明示的に指定されている 列数) - (stickyな列数))
      const eachResizableColumnWidth =
        resizableColumnsSpaceWidth /
        (useTableContentHeader.contentProperties.length -
          useTableContentHeader.contentProperties.filter((item) => !item.isVariableWidth && item.headerWidth).length -
          useTableContentHeader.options.stableColumnCount);
      for (const [index, contentProperty] of state.tableContentProperties?.contentProperties?.entries()) {
        // TODO: ここら辺複雑すぎるので直したい
        // 固定列
        const stickyColumnWidth = contentProperty.headerWidth ?? STICKY_COLUMN_WIDTH;
        if (baseTableContentVM.isSticky(useTableContentHeader.options.stableColumnCount, index)) {
          // 左端に固定する列
          useTableContentHeader.contentProperties[index].headerWidth = stickyColumnWidth;
          // 累積和を計算する
          state.accumulationTableContentWidth.push(index === 0 ? 0 : state.accumulationTableContentWidth[index - 1] + stickyColumnWidth);
        } else if (contentProperty.isVariableWidth && !!eachResizableColumnWidth && eachResizableColumnWidth > STICKY_COLUMN_WIDTH) {
          // 可変列 && 基本幅より大きい値を割り振る必要がある場合はそれをセット
          // 上で計算したeachResizableColumnWidthがSTICKY_COLUMN_WIDTHを超える -> STICKY_COLUMN_WIDTHでは余白が埋まらない
          useTableContentHeader.contentProperties[index].headerWidth = eachResizableColumnWidth;
        } else {
          // 列幅を設定
          useTableContentHeader.contentProperties[index].headerWidth = stickyColumnWidth;
        }
      }
      state.tableContentProperties = useTableContentHeader;
    };

    /**
     * 選択された行のIDをstateにセットする
     * displayIdがselectedIdsに存在する時は削除、存在しない時は追加する
     * @param displayId - 選択された行のID
     */
    const onSelectedLine = (displayId: string) => {
      const index = state.selectedIds.indexOf(displayId);
      if (index === -1) {
        state.selectedIds.push(displayId);
      } else {
        state.selectedIds.splice(index, 1);
      }
    };

    /**
     * 選択された行のBusinessIdをstateにセットする
     * displayIdがselectedIdsに存在する時は削除、存在しない時は追加する
     * @param businessId - 選択された行のBusinessId
     */
    const onSelectedLineForBusinessId = (businessId: string) => {
      const index = state.selectedIds.indexOf(businessId);
      if (index === -1) {
        state.selectedIds.push(businessId);
      } else {
        state.selectedIds.splice(index, 1);
      }
      state.isAllSelected = state.selectedIds.length === state.items.length;
    };

    /**
     * テーブルに存在するチェックボックスを全選択する
     * @param useItems - チェックボックスを全選択するアイテムのリスト
     */
    const selectAllLines = (useItems: TableContentItem[]) => {
      // 全選択用のフラグを切り返る
      state.isAllSelected = !state.isAllSelected;

      // チェックボックスを空にする
      state.selectedIds = [];

      if (state.isAllSelected) {
        // チェックボックスを全選択する
        useItems.forEach((item) => {
          state.selectedIds.push(item.displayId);
        });
      }
    };

    /**
     * テーブルに存在するチェックボックスを全選択しBusinessIdをセットする
     * @param useItems - チェックボックスを全選択するアイテムのリスト
     */
    const selectAllLinesForBusinessId = (useItems: TableContentItem[]) => {
      // 全選択用のフラグを切り返る
      state.isAllSelected = !state.isAllSelected;

      // チェックボックスを空にする
      state.selectedIds = [];

      if (state.isAllSelected) {
        // チェックボックスを全選択する
        useItems.forEach((item) => {
          state.selectedIds.push(item.businessId);
        });
      }
    };

    /**
     * テーブルに存在する発注のチェックボックスを全選択する
     * @param useItems - チェックボックスを全選択するアイテムのリスト
     */
    const selectAllLinesForOrder = (useItems: TableContentItem[]) => {
      // 全選択用のフラグを切り返る
      state.isAllSelected = !state.isAllSelected;

      // チェックボックスを空にする
      state.selectedIds = [];

      if (state.isAllSelected) {
        // チェックボックスを全選択する
        useItems.forEach((item) => {
          state.selectedIds.push(item.displayId);
        });
      }
    };

    /**
     * narrowStateを発火させる
     * @param key - 絞り込むヘッダーのキー
     * @param value - 絞り込む値
     */
    const setNarrowState = (key: TableContentProperty, value?: string | number) => {
      const sortKey = key.propertyPath;
      if (key.isNarrowable && sortKey) {
        const alreadyNarrowed = state.narrowStates.find((narrowState) => {
          return objectEquals(narrowState.narrowKey, sortKey.paths);
        });
        if (alreadyNarrowed) {
          // 絞り込み状態の場合
          state.narrowStates = state.narrowStates.filter((narrowState) => {
            return !objectEquals(narrowState, alreadyNarrowed);
          });
        } else if (value) {
          // 絞り込み状態でない場合
          (state.narrowStates as NarrowState[]).push({
            narrowKey: sortKey.paths,
            narrowValue: value,
            narrowType: sortKey.propertyType,
          });
        }
      }
    };

    /**
     * 絞り込み処理
     * Note: 完全一致のみのため、比較時にはtoString()を使っている
     */
    // const narrowTableContentItems = (): TableContentItem[] => {
    //   const narrowStates = state.narrowStates;
    //   if (narrowStates.length === 0) return state.items;
    //   let output = simpleCopy(state.items);
    //   narrowStates.forEach((narrowState) => {
    //     const isNestedSortContent = narrowState.narrowKey.length > 1;
    //     if (isNestedSortContent) {
    //       // ネストされた絞り込み処理
    //       output = output.filter((item) => {
    //         const targetValue = _getNestedPropertyValue(item, narrowState.narrowKey, narrowState.narrowType);
    //         return targetValue.toString() === narrowState.narrowValue.toString();
    //       });
    //     } else if (narrowState.narrowKey.length === 1) {
    //       // 通常の絞り込み処理
    //       output = output.filter((item) => {
    //         return item[narrowState.narrowKey[0]].toString() === narrowState.narrowValue.toString();
    //       });
    //     } else if (narrowState.narrowKey.length === 0) {
    //       // キー指定が存在しないものに関しては、何もしない
    //     }
    //   });
    //   return output;
    // };

    /**
     * ネストされたプロパティの値を取得する
     * @param item - 取得対象のアイテム
     * @param propertyPaths - ネストされたプロパティのキー
     * @param propertyType - プロパティの型
     */
    const _getNestedPropertyValue = (item: TableContentItem, propertyPaths: string[], propertyType: KeyType): string | number => {
      let target = item;
      propertyPaths.forEach((key) => {
        target = target?.[key];
      });
      if (propertyType == null) return '';
      switch (propertyType) {
        case 'string':
          if (typeof target === 'string') {
            return target;
          } else {
            return '';
          }
        case 'number':
          if (typeof target === 'number') {
            return target;
          } else {
            return 0;
          }
        case 'IDate':
          if (isIDate(target)) {
            return convertIDateToDateFormat(target);
          } else {
            return '';
          }
      }
    };

    /**
     * 列幅を自由に変更するためのリサイザーが押し込まれたときの処理
     * 各イベントの監視を開始する
     * @param event - マウスイベント
     * @param index - リサイズする列のインデックス
     */
    const onMouseDownResizer = (event: MouseEvent, index: number) => {
      const currentColumnWidth = ResizeStateService.getCurrentColumnWidth(index);
      if (!currentColumnWidth) return;
      // リサイズの状態をセット
      state.resizeState.resizingIndex = index;
      state.resizeState.currentMousePageX = event.pageX;
      state.tableContentProperties.contentProperties[index].headerWidth = currentColumnWidth;
      // イベント監視
      window.addEventListener('mousemove', _onMoveResizer);
      window.addEventListener('mouseup', _onLeaveResizer);
    };

    /**
     * リサイザーを動かしたときの処理
     * @param event - マウスイベント
     */
    const _onMoveResizer = (event: MouseEvent) => {
      const { resizingIndex, currentMousePageX } = state.resizeState;
      const resizingColumnWidth = state.tableContentProperties.contentProperties[resizingIndex].headerWidth;
      const tableContainerWidth = ResizeStateService.getCurrentTableContainerWidth(state.tableType);
      if (!resizingColumnWidth || !tableContainerWidth) return;

      // 以降リサイズ処理
      const diffX = event.pageX - currentMousePageX;
      const newWidth = resizingColumnWidth + diffX;
      // 最大列幅以上に大きくできないように && テーブル全体の幅より小さくならないように（余白が生じるので）
      if ((diffX > 0 && newWidth > MAX_COLUMN_WIDTH) || (diffX < 0 && computedColumnsWidth.value + diffX < tableContainerWidth)) return;
      state.tableContentProperties.contentProperties[resizingIndex].headerWidth = newWidth;
      state.resizeState.currentMousePageX = event.pageX;
    };

    /**
     * リサイザーからマウスが離れたときの処理
     * 変更された列幅を保存し、状態のリセット及びイベント監視の終了を行う
     * @param _ -
     */
    const _onLeaveResizer = async (_: MouseEvent) => {
      // DBに保存する用のオブジェクト、画面サイズが変化した場合に対応するためにDBには「テーブル全体の幅との比率」を保存する
      const widthRatios: { [key: string]: number } = {};
      for (const [index, contentProperty] of state.tableContentProperties.contentProperties.entries()) {
        if (
          baseTableContentVM.isSticky(state.tableContentProperties.options.stableColumnCount, index) ||
          (!contentProperty.isVariableWidth && contentProperty.headerWidth)
        )
          continue;
        const width = ResizeStateService.getCurrentColumnWidth(index);
        // リサイズ後の列幅を反映
        // frを使ってよしなに描画させているので、リサイズ中以外の幅もここで正しい値を取得する
        state.tableContentProperties.contentProperties[index].headerWidth = width;
        widthRatios[contentProperty.key] =
          ResizeStateService.getResizableColumnWidthRatio(
            state.tableType,
            state.tableContentProperties.options.stableColumnCount,
            width ?? STICKY_COLUMN_WIDTH
          ) ?? 0;
      }
      // DBに保存
      await bulkSaveColumnWidthsUseCase.Execute(state.tableType, widthRatios);
      // 状態をリセット
      state.resizeState.resizingIndex = -1;
      state.resizeState.currentMousePageX = 0;
      window.removeEventListener('mousemove', _onMoveResizer);
      window.removeEventListener('mouseup', _onLeaveResizer);
    };

    /**
     * 編集モードの切り替え(親コンポーネントからの呼び出しを想定)
     * @param isEditing - 編集モードかどうか
     */
    const onToggleEditMode = (isEditing: boolean) => {
      if (!isEditing && Object.keys(state.changeEditableCellHistory).length > 0) {
        // 編集モードを抜ける際に、確認モーダルを表示
        openFailToAcquireModal();
      } else {
        // stateに変更を反映
        state.isEditing = isEditing;
      }
    };

    /**
     * テーブルを展開する
     * @param businessId - 展開するビジネスID
     */
    const expandTable = (businessId: string) => {
      const expandedItems = [];

      switch (state.tableType) {
        case 'inventoryControl':
          break;
        default:
          console.error('展開機能が未実装のTableTypeで展開Keyを指定されました');
          return;
      }

      if (expandedItems.length === 0) return;
      for (const [index, item] of state.items.entries()) {
        if (businessId === item.businessId) {
          state.items.splice(index + 1, 0, ...expandedItems);
          break;
        }
      }
    };

    /**
     * 編集可能なヘッダーのkeyとtextと表示情報のリストを取得する関数
     * @param tableType - テーブルのタイプ
     * @param propTableContentProperties - 初期のテーブル情報
     */
    const getModifiableHeaderList = (tableType: TableType, propTableContentProperties: TableContentHeader) => {
      const res: HeaderList = [];
      if (isHeaderModifiableTableType(tableType) && propTableContentProperties.contentProperties) {
        const modifiableHeaderKeys = GetModifiableHeaderKeysUseCase.Execute(tableType) ?? [];
        const modifiableHeaders = propTableContentProperties.contentProperties.filter(
          (header) => modifiableHeaderKeys.includes(header.key) || isAggregateBusinessFreeColumnKey(header.key)
        );
        modifiableHeaders.map((header) => {
          res.push({
            key: header.key,
            text: header.displayLabel ?? _getHeaderName(header.key),
            isShown: baseTableContentVM.getCurrentHeaderList(state.tableContentProperties).includes(header.key),
          });
        });
      }
      return res;
    };

    /**
     * ヘッダーのテキストを取得する関数
     * @param key - ヘッダーのキー
     * @returns - ヘッダーのテキスト
     */
    const _getHeaderName = (key: string) => {
      const t = i18n.t;
      if (isCommonTableHeaderKey(key)) {
        return t(key);
      } else if (isOrderHeaderKey(key)) {
        return t(key);
      } else if (isOrderControlHeaderKey(key)) {
        return t(key);
      } else if (isForecastForReceiptKey(key)) {
        return t(new ForecastForReceiptTableContentViewModel().getHeaderText(key));
      } else if (isForecastAccuracyKey(key)) {
        return t(new ForecastAccuracyTableContentViewModel().getHeaderText(key));
      } else if (isUnmanagedBusinessHeaderKey(key)) {
        return new UnmanagedBusinessContentViewModel().getAnnounceYearMonth(key);
      } else if (isBusinessesForSupplierForecastHeaderKey(key)) {
        return t(new BusinessesForSupplierForecastTableContentViewModel().getHeaderText(key, undefined));
      } else if (isTrashBinHeaderKey(key)) {
        return t(new TrashBinTableContentViewModel().getHeaderText(key, undefined) ?? '');
      }
      return '';
    };

    /**
     * ヘッダーの表示/非表示情報をDBに保存する
     * @param tableType - テーブルのタイプ
     * @param header - 変更対象のヘッダー
     * @param isHide - 非表示にするかどうか
     */
    const _updateDisplayHeader = async (tableType: TableType, header: string, isHide: boolean) => {
      await diContainer.saveTableHeaderHidePreferenceUseCase.Execute(tableType, header, isHide);
    };

    /**
     * ヘッダーの表示/非表示情報をまとめてDBに保存する
     * @param tableType - テーブルのタイプ
     * @param headers - 変更対象のヘッダーリスト
     * @param isHide - 非表示にするかどうか
     */
    const _bulkUpdateDisplayHeader = async (tableType: TableType, headers: string[], isHide: boolean) => {
      await diContainer.bulkSaveTableHeaderHidePreferenceUseCase.Execute(tableType, headers, isHide);
    };

    /**
     * ヘッダー編集モーダル表示/非表示操作時の処理
     * @param tableType - テーブルのタイプ
     * @param key - 削除するヘッダーのkey
     * @param propContentProperties - Propで渡されたヘッダー情報
     * @param isHide - 非表示にするか否か
     */
    const onToggleDisplayHeader = async (
      tableType: TableType,
      key: string,
      propContentProperties: TableContentProperty[],
      isHide: boolean
    ) => {
      await _updateDisplayHeader(tableType, key, isHide);
      await _initializeColumnWidth(propContentProperties);
    };

    return {
      state,
      $reset,
      initializeTableContent,
      saveTableHeaderPreference,
      synchronizeTableContentProperties,
      onSelectedLine,
      onSelectedLineForBusinessId,
      selectAllLines,
      selectAllLinesForBusinessId,
      selectAllLinesForOrder,
      setNarrowState,
      onMouseDownResizer,
      onToggleEditMode,
      getModifiableHeaderList,
      onToggleDisplayHeader,
    };
  });
